// Builds the POST body sent to `/admin-panel-v2/users/exports` from the
// user-facing list filters. The BE 422s on any key outside its vocabulary, so
// this mapper is an *allow-list* — never a literal spread of the form store.
// Two concerns live here:
//
//   1. `buildUsersExportPayload(filters)` — strip pagination/sort/id filters
//      that can't be exported, keep the accepted vocabulary, and drop empty
//      values so the body stays minimal.
//   2. `getUnexportableFilterKeys(filters)` — surface any active filter that
//      is *not* allowed for export, so the UI can disable the button with a
//      precise tooltip instead of round-tripping to hit the 422.
//
// The BE remains the source of truth: this file is a UX mirror of it.

import type {
  UsersExportRequest,
  UsersFilterFormValues,
  UsersListParams,
} from "../types/users";

/** Filter keys that CAN be exported (mirror of the v1 list vocabulary). */
export const EXPORTABLE_FILTER_KEYS = [
  "registration_date_start",
  "registration_date_end",
  "status",
  "kyc_status",
  "ftd",
  "registration_state",
] as const;

/**
 * Filter keys that cannot be included in an export. The list-endpoint accepts
 * them; the export endpoint 422s on them (`type: "extra_forbidden"`).
 *
 * `search` is not a form field on the users list — it lives on the free-text
 * search-bar state — but it's included here so callers using the string can
 * detect an active free-text search without inventing a second key.
 */
export const UNEXPORTABLE_FILTER_KEYS = [
  "search",
  "sports_bet_id",
  "casino_bet_id",
  "transaction_id",
] as const;

type ExportableKey = (typeof EXPORTABLE_FILTER_KEYS)[number];
type UnexportableKey = (typeof UNEXPORTABLE_FILTER_KEYS)[number];

/** Union of everything the caller can pass in — `Record` keeps it flexible
 * for tests that want to poke edge cases (unknown keys, wrong types). */
export type UsersExportFilterInput = UsersFilterFormValues &
  Partial<UsersListParams> & {
    // Not on the list form — passed separately by the UserTable search bar.
    search?: string | null;
  };

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === "string" && v.trim().length > 0;

/**
 * Extract only the export-allowed keys from the list filters, trimming
 * strings and dropping empty values. Unknown keys never pass through — the
 * output is a superset only of `UsersExportRequest`. `columns` is left to
 * the caller because it's not part of the list filter shape.
 */
export function buildUsersExportPayload(
  filters: UsersExportFilterInput | null | undefined,
  overrides: { columns?: string[] } = {},
): UsersExportRequest {
  const body: UsersExportRequest = {};
  if (!filters) return applyColumns(body, overrides.columns);

  for (const key of EXPORTABLE_FILTER_KEYS) {
    const raw = filters[key as keyof UsersExportFilterInput];
    if (!isNonEmptyString(raw)) continue;
    body[key as ExportableKey] = raw.trim();
  }

  return applyColumns(body, overrides.columns);
}

function applyColumns(
  body: UsersExportRequest,
  columns?: string[],
): UsersExportRequest {
  if (!columns) return body;
  // Empty arrays are their own 422 case — pass through so the BE surfaces it.
  return { ...body, columns };
}

/**
 * Return the list of currently-active filter keys that would be rejected by
 * the export endpoint. UI uses this to disable the download button + tooltip
 * ("Search and ID filters can't be included in exports — clear them to
 * download"). Order matches `UNEXPORTABLE_FILTER_KEYS` so callers can format
 * the message deterministically.
 */
export function getUnexportableFilterKeys(
  filters: UsersExportFilterInput | null | undefined,
): UnexportableKey[] {
  if (!filters) return [];
  const active: UnexportableKey[] = [];
  for (const key of UNEXPORTABLE_FILTER_KEYS) {
    const raw = filters[key as keyof UsersExportFilterInput];
    if (isNonEmptyString(raw)) active.push(key);
  }
  return active;
}

/** Convenience wrapper — `true` when at least one unexportable filter is set. */
export const hasUnexportableFilters = (
  filters: UsersExportFilterInput | null | undefined,
): boolean => getUnexportableFilterKeys(filters).length > 0;
